// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scale, Filter, Archive, Building2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { MobileSearchBar } from '../ui/MobileSearchBar';
import { MobileListItem } from '../ui/MobileListItem';
import { MobileEmptyState } from '../ui/MobileEmptyState';
import { MobilePageContainer } from '../ui/MobilePageContainer';
import { MobileFab } from '../ui/MobileFab';
import MobileCaseQuickForm from './MobileCaseQuickForm';

type CaseRow = {
  id: string;
  title: string;
  process_type?: 'case' | 'administrative';
  case_number?: string;
  case_number_cnj?: string;
  case_type?: string;
  process_number?: string;
  administrative_agency?: string;
  procedure_type?: string;
  status?: string;
  priority?: string;
  risk_classification?: string;
  is_administrative_process?: boolean;
  archived?: boolean;
  client_id?: string;
  clients?: { name?: string } | null;
};

const STATUS_LABELS: Record<string, { label: string; tone: string }> = {
  active: { label: 'Em andamento', tone: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  em_andamento: { label: 'Em andamento', tone: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  suspended: { label: 'Suspenso', tone: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  concluded: { label: 'Concluído', tone: 'bg-muted text-muted-foreground' },
  closed: { label: 'Encerrado', tone: 'bg-muted text-muted-foreground' },
  archived: { label: 'Arquivado', tone: 'bg-muted text-muted-foreground' },
};

const PRIORITY_TONE: Record<string, string> = {
  alta: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
  high: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
  média: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  media: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  medium: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  baixa: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
  low: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
};

const MobileCases = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const [cases, setCases] = useState<CaseRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [typeFilter, setTypeFilter] = useState<'all' | 'judicial' | 'administrative'>('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const baseSelect =
        'id, title, case_number, case_number_cnj, case_type, status, priority, risk_classification, archived, client_id, clients(name)';
      const adminSelect = `${baseSelect}, is_administrative_process`;

      let { data, error } = await supabase
        .from('cases')
        .select(adminSelect)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(200);

      // Compatibilidade com bancos que ainda não têm a coluna is_administrative_process.
      if (error && /is_administrative_process/i.test(error.message || '')) {
        const fallback = await supabase
          .from('cases')
          .select(baseSelect)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(200);
        data = (fallback.data || []).map((row: any) => ({
          ...row,
          is_administrative_process: false,
        }));
        error = fallback.error;
      }

      if (error) throw error;

      const { data: adminProcesses, error: adminError } = await supabase
        .from('administrative_processes')
        .select('id, title, description, process_number, administrative_agency, procedure_type, status, priority, created_at, user_id')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(200);

      if (adminError) throw adminError;

      const regularCases = (data || []).map((row: any) => ({
        ...row,
        process_type: 'case' as const,
      }));

      const administrativeCases = (adminProcesses || []).map((row: any) => ({
        ...row,
        process_type: 'administrative' as const,
        case_number: row.process_number || null,
        case_number_cnj: null,
        case_type: 'Processo Administrativo',
        risk_classification: null,
        is_administrative_process: true,
        archived: false,
        client_id: null,
        clients: null,
      }));

      const merged = [...regularCases, ...administrativeCases].sort((a: any, b: any) =>
        String(b.created_at || '').localeCompare(String(a.created_at || ''))
      );

      setCases(merged);
    } catch (err: any) {
      toast({
        title: 'Erro ao carregar processos',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cases.filter((c) => {
      const isAdministrative = c.process_type === 'administrative';

      if (statusFilter === 'active' && c.archived) return false;
      if (statusFilter === 'archived' && !c.archived) return false;
      if (typeFilter === 'judicial' && isAdministrative) return false;
      if (typeFilter === 'administrative' && !isAdministrative) return false;
      if (!q) return true;
      const haystack = [
        c.title,
        c.case_number,
        c.case_number_cnj,
        c.process_number,
        c.administrative_agency,
        c.procedure_type,
        c.clients?.name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [cases, search, statusFilter, typeFilter]);

  return (
    <MobilePageContainer>
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur px-3 pt-3 pb-2 border-b border-border">
        <div className="flex gap-2 items-center">
          <MobileSearchBar
            value={search}
            onChange={setSearch}
            placeholder="Buscar processo, nº ou cliente..."
            className="flex-1"
          />
          <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="h-11 w-11 shrink-0">
                <Filter className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="rounded-t-2xl">
              <SheetHeader>
                <SheetTitle>Filtros</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">
                    Status
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { id: 'active', label: 'Ativos' },
                      { id: 'archived', label: 'Arquivados' },
                      { id: 'all', label: 'Todos' },
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setStatusFilter(opt.id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          statusFilter === opt.id
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-card border-border'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">
                    Tipo
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { id: 'all', label: 'Todos' },
                      { id: 'judicial', label: 'Judicial' },
                      { id: 'administrative', label: 'Administrativo' },
                    ].map((opt: any) => (
                      <button
                        key={opt.id}
                        onClick={() => setTypeFilter(opt.id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          typeFilter === opt.id
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-card border-border'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
        <div className="flex items-center justify-between mt-2 px-1 text-[11px] text-muted-foreground">
          <span>
            {loading ? 'Carregando...' : `${filtered.length} processo${filtered.length !== 1 ? 's' : ''}`}
          </span>
        </div>
      </div>

      {/* Lista */}
      {!loading && filtered.length === 0 ? (
        <MobileEmptyState
          className="mt-8"
          icon={<Scale className="h-6 w-6" />}
          title="Nenhum processo encontrado"
          description={
            search
              ? 'Ajuste a busca ou os filtros para ver mais resultados.'
              : 'Cadastre seu primeiro processo no botão +.'
          }
          action={
            !search && (
              <Button onClick={() => setCreateOpen(true)}>Novo processo</Button>
            )
          }
        />
      ) : (
        <div className="divide-y divide-border bg-card">
          {filtered.map((c) => {
            const st = STATUS_LABELS[(c.status || '').toLowerCase()];
            const prTone = PRIORITY_TONE[(c.priority || '').toLowerCase()];
            const isAdmin = c.process_type === 'administrative';
            return (
              <MobileListItem
                key={c.id}
                icon={
                  isAdmin ? (
                    <Building2 className="h-5 w-5" />
                  ) : c.archived ? (
                    <Archive className="h-5 w-5" />
                  ) : (
                    <Scale className="h-5 w-5" />
                  )
                }
                iconBg={
                  isAdmin
                    ? 'bg-sky-500/15 text-sky-600 dark:text-sky-400'
                    : c.archived
                    ? 'bg-muted text-muted-foreground'
                    : 'bg-primary/15 text-primary'
                }
                title={c.title || 'Sem título'}
                subtitle={
                  <>
                    {c.case_number_cnj || c.case_number || c.process_number ? (
                      <span className="font-mono">
                        {c.case_number_cnj || c.case_number || c.process_number}
                      </span>
                    ) : null}
                    {isAdmin && c.administrative_agency && (
                      <span>
                        {(c.case_number_cnj || c.case_number || c.process_number) ? ' · ' : ''}
                        {c.administrative_agency}
                      </span>
                    )}
                    {c.clients?.name && (
                      <span>
                        {(c.case_number_cnj || c.case_number || c.process_number || (isAdmin && c.administrative_agency)) ? ' · ' : ''}
                        {c.clients.name}
                      </span>
                    )}
                  </>
                }
                badges={
                  <>
                    {st && (
                      <Badge className={`${st.tone} border-0 text-[10px]`}>
                        {st.label}
                      </Badge>
                    )}
                    {prTone && c.priority && (
                      <Badge className={`${prTone} border-0 text-[10px]`}>
                        {c.priority}
                      </Badge>
                    )}
                  </>
                }
                onClick={() => {
                  if (isAdmin) {
                    navigate(`/pwa/processo-administrativo/${c.id}`);
                    return;
                  }
                  navigate(`/pwa/processo/${c.id}`);
                }}
              />
            );
          })}
        </div>
      )}

      <MobileFab onClick={() => setCreateOpen(true)} label="Novo" />

      <MobileCaseQuickForm
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(caseId) => {
          load();
          navigate(`/pwa/processo/${caseId}`);
        }}
      />
    </MobilePageContainer>
  );
};

export default MobileCases;
